// Muxum cart drawer — Cart AJAX wiring + open/close + line edits + upsell.
// Listens for `muxum:cart:open` and exposes `muxum:cart:updated` events.
(() => {
  const drawer = document.querySelector('[data-muxum-cart-drawer]');
  const overlay = document.querySelector('[data-muxum-cart-overlay]');
  if (!drawer || !overlay) return;

  const FREE_SHIP_MXN = parseInt(drawer.dataset.freeShipThreshold, 10) || 1000;
  const FREE_SHIP_CENTS = FREE_SHIP_MXN * 100;
  const closeBtn = drawer.querySelector('[data-muxum-cart-close]');

  // ---- Open / close -------------------------------------------------------
  const openDrawer = () => {
    drawer.hidden = false;
    overlay.hidden = false;
    void drawer.offsetWidth;
    drawer.dataset.open = 'true';
    overlay.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    drawer.dataset.open = 'false';
    overlay.dataset.open = 'false';
    document.body.style.overflow = '';
    setTimeout(() => {
      if (drawer.dataset.open === 'false') {
        drawer.hidden = true;
        overlay.hidden = true;
      }
    }, 300);
  };

  document.addEventListener('muxum:cart:open', openDrawer);
  // Re-render the drawer when other components (PDP form, upsell, etc.)
  // signal that the cart was mutated outside of this module's flows.
  document.addEventListener('muxum:cart:updated', (e) => {
    // If the event already carries cart detail (dispatched after our own rerender),
    // skip refetching to avoid an infinite loop.
    if (e.detail?.cart) return;
    rerender();
  });
  closeBtn?.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.dataset.open === 'true') closeDrawer();
  });

  // ---- Money formatter (MXN, no decimals) ---------------------------------
  const fmt = (cents) => `$${Math.round(cents / 100).toLocaleString('es-MX')}`;

  // ---- Render after each cart change --------------------------------------
  const fetchCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const rerender = async () => {
    const cart = await fetchCart();
    // Best path: refetch the rendered drawer section markup.
    try {
      const res = await fetch('/?section_id=cart-drawer', { credentials: 'same-origin' });
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newDrawer = tmp.querySelector('[data-muxum-cart-drawer]');
      const newOverlay = tmp.querySelector('[data-muxum-cart-overlay]');
      if (newDrawer && newOverlay) {
        // Preserve open state.
        const wasOpen = drawer.dataset.open === 'true';
        // Replace inner content only (keep root nodes so listeners on drawer still apply).
        drawer.innerHTML = newDrawer.innerHTML;
        // Re-bind because new buttons exist.
        bindLineHandlers();
        bindUpsellHandler();
        const newCloseBtn = drawer.querySelector('[data-muxum-cart-close]');
        newCloseBtn?.addEventListener('click', closeDrawer);
        if (wasOpen) {
          drawer.dataset.open = 'true';
          overlay.dataset.open = 'true';
        }
      }
    } catch {
      // Fallback: minimal in-place updates.
      updateTotalsInPlace(cart);
    }

    document.dispatchEvent(
      new CustomEvent('muxum:cart:updated', {
        detail: { itemCount: cart.item_count, totalPrice: cart.total_price, cart },
      })
    );
  };

  const updateTotalsInPlace = (cart) => {
    const subtotalEl = drawer.querySelector('[data-muxum-cart-subtotal]');
    const totalEl = drawer.querySelector('[data-muxum-cart-total]');
    const countEl = drawer.querySelector('[data-muxum-cart-count-text]');
    if (subtotalEl) subtotalEl.textContent = `${fmt(cart.items_subtotal_price)} MXN`;
    if (totalEl) totalEl.textContent = `${fmt(cart.total_price)} MXN`;
    if (countEl) countEl.textContent = cart.item_count;

    // Ship bar
    const fill = drawer.querySelector('[data-muxum-shipbar-fill]');
    const text = drawer.querySelector('[data-muxum-shipbar-text]');
    const amounts = drawer.querySelector('[data-muxum-shipbar-amounts]');
    const bar = drawer.querySelector('[data-muxum-shipbar]');
    if (bar) bar.hidden = cart.item_count === 0;
    const pct = Math.min(100, Math.round((cart.total_price / FREE_SHIP_CENTS) * 100));
    if (fill) fill.style.width = `${pct}%`;
    if (amounts) amounts.textContent = `${fmt(cart.total_price)} / $${FREE_SHIP_MXN}`;
    if (text) {
      if (cart.total_price >= FREE_SHIP_CENTS) {
        text.innerHTML = '✓ ¡Tu envío es gratis!';
        bar?.classList.add('is-complete');
      } else {
        const remaining = FREE_SHIP_CENTS - cart.total_price;
        text.innerHTML = `Te faltan <strong>${fmt(remaining)}</strong> para envío gratis 🚚`;
        bar?.classList.remove('is-complete');
      }
    }
  };

  // ---- Cart line handlers -------------------------------------------------
  const changeLine = async (lineEl, quantity) => {
    const key = lineEl.dataset.muxumLineKey;
    if (!key) return;
    lineEl.classList.add('is-updating');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('cart change failed');
      await rerender();
    } catch (e) {
      console.error('Muxum cart change failed', e);
    } finally {
      lineEl.classList.remove('is-updating');
    }
  };

  const bindLineHandlers = () => {
    drawer.querySelectorAll('[data-muxum-line-key]').forEach((line) => {
      const qtyEl = line.querySelector('[data-muxum-line-qty]');
      const dec = line.querySelector('[data-muxum-line-decrease]');
      const inc = line.querySelector('[data-muxum-line-increase]');
      const rm = line.querySelector('[data-muxum-line-remove]');
      const currentQty = () => parseInt(qtyEl?.textContent || '1', 10);

      dec?.addEventListener('click', () => changeLine(line, Math.max(0, currentQty() - 1)));
      inc?.addEventListener('click', () => changeLine(line, currentQty() + 1));
      rm?.addEventListener('click', () => changeLine(line, 0));
    });
  };

  // ---- Upsell -------------------------------------------------------------
  const bindUpsellHandler = () => {
    const upsellBtn = drawer.querySelector('[data-muxum-upsell-add]');
    upsellBtn?.addEventListener('click', async () => {
      const variantId = upsellBtn.dataset.variantId;
      if (!variantId) return;
      upsellBtn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!res.ok) throw new Error('upsell add failed');
        await rerender();
      } catch (e) {
        console.error('Muxum upsell add failed', e);
        upsellBtn.disabled = false;
      }
    });
  };

  // ---- Add-to-cart from anywhere ------------------------------------------
  // Any form/button with data-muxum-add-to-cart triggers cart open + add.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-muxum-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    const sellingPlanId = btn.dataset.sellingPlanId;
    const quantity = parseInt(btn.dataset.quantity || '1', 10);
    if (!variantId) return;

    btn.disabled = true;
    const orig = btn.textContent;
    btn.textContent = 'Añadiendo…';
    try {
      const body = { id: variantId, quantity };
      if (sellingPlanId) body.selling_plan = sellingPlanId;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('add to cart failed');
      await rerender();
      openDrawer();
    } catch (err) {
      console.error('Muxum add to cart failed', err);
    } finally {
      btn.disabled = false;
      btn.textContent = orig;
    }
  });

  // Initial bind for SSR-rendered DOM.
  bindLineHandlers();
  bindUpsellHandler();
})();
